import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../components/ui/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Input } from '../components/ui/FormField';
import { 
  Settings as SettingsIcon, 
  DollarSign, 
  Navigation, 
  Phone, 
  Mail,
  Save,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  Percent,
  Wallet,
  FileText,
  Shield,
  Globe,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const Section = ({ id, title, open, onToggle, children }) => (
  <Card>
    <button
      type="button"
      onClick={() => onToggle(id)}
      className="w-full flex items-center justify-between text-left -m-1 p-1 rounded-lg hover:bg-slate-50 transition"
    >
      <div className="flex-1">{title}</div>
      {open
        ? <ChevronDown className="w-5 h-5 text-slate-500" />
        : <ChevronRight className="w-5 h-5 text-slate-500" />}
    </button>
    {open && <div className="mt-4">{children}</div>}
  </Card>
);

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const { user: currentAdmin } = useAuth();
  const canManageSettings = hasPermission('view_settings');

  const [basePrice, setBasePrice] = useState(3);
  const [maxDeliveryDistance, setMaxDeliveryDistance] = useState(100);
  const [minPrice, setMinPrice] = useState(1.5);
  const [deductionFixed, setDeductionFixed] = useState(0.1);
  const [deductionMode, setDeductionMode] = useState('fixed');
  const [deductionPercentage, setDeductionPercentage] = useState(10);
  const [driverMinBalance, setDriverMinBalance] = useState(-5);
  const [welcomeBonus, setWelcomeBonus] = useState(2);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [termsCustomerEN, setTermsCustomerEN] = useState('');
  const [termsDriverEN, setTermsDriverEN] = useState('');
  const [privacyCustomerEN, setPrivacyCustomerEN] = useState('');
  const [privacyDriverEN, setPrivacyDriverEN] = useState('');
  const [termsCustomerAR, setTermsCustomerAR] = useState('');
  const [termsDriverAR, setTermsDriverAR] = useState('');
  const [privacyCustomerAR, setPrivacyCustomerAR] = useState('');
  const [privacyDriverAR, setPrivacyDriverAR] = useState('');

  const [openSections, setOpenSections] = useState({
    summary: true,
    pricing: false,
    financial: false,
    support: false,
    terms: false,
    privacy: false,
  });
  const toggleSection = (id) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const appConfigRef = doc(db, 'app_settings', 'app_config');
      const appConfigSnap = await getDoc(appConfigRef);
      if (appConfigSnap.exists()) {
        const data = appConfigSnap.data();
        setBasePrice(data.basePrice || 3);
        setMaxDeliveryDistance(data.maxDeliveryDistance || 100);
        setMinPrice(data.minPrice || 1.5);
      }

      const financialConfigRef = doc(db, 'app_settings', 'financial_config');
      const financialConfigSnap = await getDoc(financialConfigRef);
      if (financialConfigSnap.exists()) {
        const data = financialConfigSnap.data();
        setDeductionFixed(data.deduction_fixed ?? 0.1);
        setDeductionMode(data.deduction_mode ?? 'fixed');
        setDeductionPercentage(data.deduction_percentage ?? 10);
        setDriverMinBalance(data.driver_min_balance ?? -5);
        setWelcomeBonus(data.welcome_bonus ?? 2);
      }

      const supportContactsRef = doc(db, 'app_settings', 'support_contacts');
      const supportContactsSnap = await getDoc(supportContactsRef);
      if (supportContactsSnap.exists()) {
        const data = supportContactsSnap.data();
        setSupportEmail(data.email || '');
        setSupportPhone(data.phone || '');
      }

      const termsRef = doc(db, 'app_settings', 'Terms&Policy');
      const termsSnap = await getDoc(termsRef);
      if (termsSnap.exists()) {
        const data = termsSnap.data();
        setTermsCustomerEN(data['Terms&ConditionsCustomer']?.textEN || '');
        setTermsCustomerAR(data['Terms&ConditionsCustomer']?.textAR || '');
        setTermsDriverEN(data['Terms&ConditionsDriver']?.textEN || '');
        setTermsDriverAR(data['Terms&ConditionsDriver']?.textAR || '');
        setPrivacyCustomerEN(data.PrivacyPolicyCustomer?.textEN || '');
        setPrivacyCustomerAR(data.PrivacyPolicyCustomer?.textAR || '');
        setPrivacyDriverEN(data.PrivacyPolicyDriver?.textEN || '');
        setPrivacyDriverAR(data.PrivacyPolicyDriver?.textAR || '');
      }

    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!canManageSettings) {
      toast.error('You do not have permission to manage settings');
      return;
    }
    if (basePrice <= 0 || minPrice <= 0 || maxDeliveryDistance <= 0) {
      toast.error('Invalid pricing configuration');
      return;
    }
    if (deductionFixed < 0 || deductionPercentage < 0 || deductionPercentage > 100 || welcomeBonus < 0) {
      toast.error('Invalid financial configuration');
      return;
    }

    try {
      setSaving(true);
      await setDoc(doc(db, 'app_settings', 'app_config'), {
        basePrice: Number(basePrice),
        maxDeliveryDistance: Number(maxDeliveryDistance),
        minPrice: Number(minPrice),
        updatedAt: serverTimestamp()
      }, { merge: true });
      await setDoc(doc(db, 'app_settings', 'financial_config'), {
        deduction_fixed: Number(deductionFixed),
        deduction_mode: deductionMode,
        deduction_percentage: Number(deductionPercentage),
        driver_min_balance: Number(driverMinBalance),
        welcome_bonus: Number(welcomeBonus),
        updatedAt: serverTimestamp()
      }, { merge: true });
      await setDoc(doc(db, 'app_settings', 'support_contacts'), {
        email: supportEmail,
        phone: supportPhone,
        updatedAt: serverTimestamp()
      }, { merge: true });
      await setDoc(doc(db, 'app_settings', 'Terms&Policy'), {
        'Terms&ConditionsCustomer': { textEN: termsCustomerEN, textAR: termsCustomerAR, updateAt: serverTimestamp() },
        'Terms&ConditionsDriver': { textEN: termsDriverEN, textAR: termsDriverAR, updateAt: serverTimestamp() },
        'PrivacyPolicyCustomer': { textEN: privacyCustomerEN, textAR: privacyCustomerAR, updateAt: serverTimestamp() },
        'PrivacyPolicyDriver': { textEN: privacyDriverEN, textAR: privacyDriverAR, updateAt: serverTimestamp() }
      }, { merge: true });
      logAdminActivity({
        actor: currentAdmin, action: 'update_settings',
        targetType: 'settings', targetId: 'app_settings',
        targetLabel: 'Settings',
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <SettingsIcon className="w-8 h-8 text-primary-600" />
            App Settings
          </h1>
          <p className="text-slate-600 mt-1">Configure application settings and parameters</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} disabled={loading || saving}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="primary" onClick={saveSettings} disabled={loading || saving || !canManageSettings}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {!canManageSettings && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-800 font-medium">No Access</p>
            <p className="text-yellow-700 text-sm mt-1">You don't have permission to view or modify settings.</p>
          </div>
        </div>
      )}
      <Section
        id="summary"
        open={openSections.summary}
        onToggle={toggleSection}
        title={<h2 className="text-xl font-semibold">Configuration Summary</h2>}
      >
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-600 font-medium">Active Deduction Mode</p>
              <p className="text-2xl font-bold text-blue-900 mt-1 capitalize">{deductionMode}</p>
              <p className="text-sm text-blue-700 mt-1">{deductionMode === 'fixed' ? `${deductionFixed} JOD per order` : `${deductionPercentage}% of order value`}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600 font-medium">Price Range</p>
              <p className="text-2xl font-bold text-green-900 mt-1">{minPrice} - {basePrice} JOD</p>
              <p className="text-sm text-green-700 mt-1">Min to Base price</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600 font-medium">Max Distance</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">{maxDeliveryDistance} km</p>
              <p className="text-sm text-purple-700 mt-1">Maximum delivery range</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-orange-600 font-medium">Welcome Bonus</p>
              <p className="text-2xl font-bold text-orange-900 mt-1">{welcomeBonus} JOD</p>
              <p className="text-sm text-orange-700 mt-1">For new users</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-red-600 font-medium">Min Driver Balance</p>
              <p className="text-2xl font-bold text-red-900 mt-1">{driverMinBalance} JOD</p>
              <p className="text-sm text-red-700 mt-1">Minimum allowed balance</p>
            </div>
            <div className="bg-cyan-50 p-4 rounded-lg">
              <p className="text-sm text-cyan-600 font-medium">Support Contact</p>
              <p className="text-lg font-bold text-cyan-900 mt-1 truncate">{supportPhone || 'Not set'}</p>
              <p className="text-sm text-cyan-700 mt-1 truncate">{supportEmail || 'Not set'}</p>
            </div>
          </div>
        </div>
      </Section>

      <Section
        id="pricing"
        open={openSections.pricing}
        onToggle={toggleSection}
        title={
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-primary-600" />
            Delivery Pricing Configuration
          </h2>
        }
      >
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700">Base Price (JOD) *</label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="number" min="0" step="0.01" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} disabled={!canManageSettings} className="pl-10" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Minimum Price (JOD) *</label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="number" min="0" step="0.01" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} disabled={!canManageSettings} className="pl-10" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Max Distance (km) *</label>
              <div className="relative mt-1">
                <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="number" min="0" step="0.1" value={maxDeliveryDistance} onChange={(e) => setMaxDeliveryDistance(e.target.value)} disabled={!canManageSettings} className="pl-10" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section
        id="financial"
        open={openSections.financial}
        onToggle={toggleSection}
        title={
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Wallet className="w-6 h-6 text-primary-600" />
            Financial Configuration
          </h2>
        }
      >
        <div>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                Deduction Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Deduction Mode *</label>
                  <select value={deductionMode} onChange={(e) => setDeductionMode(e.target.value)} disabled={!canManageSettings} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                    <option value="fixed">Fixed Amount</option>
                    <option value="percentage">Percentage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Fixed Deduction (JOD)</label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input type="number" min="0" step="0.01" value={deductionFixed} onChange={(e) => setDeductionFixed(e.target.value)} disabled={!canManageSettings || deductionMode !== 'fixed'} className="pl-10" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Percentage Deduction (%)</label>
                  <div className="relative mt-1">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input type="number" min="0" max="100" step="0.1" value={deductionPercentage} onChange={(e) => setDeductionPercentage(e.target.value)} disabled={!canManageSettings || deductionMode !== 'percentage'} className="pl-10" />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-600" />
                Driver Balance & Bonuses
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Driver Minimum Balance (JOD)</label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input type="number" step="0.01" value={driverMinBalance} onChange={(e) => setDriverMinBalance(e.target.value)} disabled={!canManageSettings} className="pl-10" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Welcome Bonus (JOD)</label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input type="number" min="0" step="0.01" value={welcomeBonus} onChange={(e) => setWelcomeBonus(e.target.value)} disabled={!canManageSettings} className="pl-10" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section
        id="support"
        open={openSections.support}
        onToggle={toggleSection}
        title={
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Phone className="w-6 h-6 text-primary-600" />
            Support Contacts
          </h2>
        }
      >
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700">Support Email</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} disabled={!canManageSettings} placeholder="support@example.com" className="pl-10" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Support Phone</label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input type="tel" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} disabled={!canManageSettings} placeholder="+213555123456" className="pl-10" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section
        id="terms"
        open={openSections.terms}
        onToggle={toggleSection}
        title={
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary-600" />
            Terms & Conditions
          </h2>
        }
      >
        <div>
          <div className="space-y-8">
            <div>
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Customer Terms & Conditions
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">English Version</label>
                  <textarea value={termsCustomerEN} onChange={(e) => setTermsCustomerEN(e.target.value)} disabled={!canManageSettings} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="Enter terms and conditions for customers in English..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">النسخة العربية (Arabic Version)</label>
                  <textarea value={termsCustomerAR} onChange={(e) => setTermsCustomerAR(e.target.value)} disabled={!canManageSettings} rows={8} dir="rtl" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="أدخل الشروط والأحكام للعملاء بالعربية..." />
                </div>
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Driver Terms & Conditions
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">English Version</label>
                  <textarea value={termsDriverEN} onChange={(e) => setTermsDriverEN(e.target.value)} disabled={!canManageSettings} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="Enter terms and conditions for drivers in English..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">النسخة العربية (Arabic Version)</label>
                  <textarea value={termsDriverAR} onChange={(e) => setTermsDriverAR(e.target.value)} disabled={!canManageSettings} rows={8} dir="rtl" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="أدخل الشروط والأحكام للسائقين بالعربية..." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section
        id="privacy"
        open={openSections.privacy}
        onToggle={toggleSection}
        title={
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary-600" />
            Privacy Policies
          </h2>
        }
      >
        <div>
          <div className="space-y-8">
            <div>
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                Customer Privacy Policy
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">English Version</label>
                  <textarea value={privacyCustomerEN} onChange={(e) => setPrivacyCustomerEN(e.target.value)} disabled={!canManageSettings} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="Enter privacy policy for customers in English..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">النسخة العربية (Arabic Version)</label>
                  <textarea value={privacyCustomerAR} onChange={(e) => setPrivacyCustomerAR(e.target.value)} disabled={!canManageSettings} rows={8} dir="rtl" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="أدخل سياسة الخصوصية للعملاء بالعربية..." />
                </div>
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                Driver Privacy Policy
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">English Version</label>
                  <textarea value={privacyDriverEN} onChange={(e) => setPrivacyDriverEN(e.target.value)} disabled={!canManageSettings} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="Enter privacy policy for drivers in English..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">النسخة العربية (Arabic Version)</label>
                  <textarea value={privacyDriverAR} onChange={(e) => setPrivacyDriverAR(e.target.value)} disabled={!canManageSettings} rows={8} dir="rtl" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 font-mono text-sm" placeholder="أدخل سياسة الخصوصية للسائقين بالعربية..." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

    </div>
  );
};

export default Settings;